/** 快捷键展示用的按键文案（界面芯片） */
export const KEYS = {
  ALT: 'Alt / ⌥',
  WIN: 'Win / ⌘',
  SHIFT: 'Shift',
  SLASH: '/',
  UP: 'Up',
  DOWN: 'Down',
  ESC: 'Esc',
};

/**
 * 稳定的快捷键动作 ID。
 * 绑定点、弹窗行、ui_settings 覆盖表都使用这些常量，避免硬编码字符串漂移。
 */
export const SHORTCUT_ACTIONS = {
  /** 打开上一通会话 */
  OPEN_PREVIOUS_CONVERSATION: 'OPEN_PREVIOUS_CONVERSATION',
  /** 打开下一通会话 */
  OPEN_NEXT_CONVERSATION: 'OPEN_NEXT_CONVERSATION',
  /** 解决当前会话并跳到下一通 */
  RESOLVE_AND_NEXT: 'RESOLVE_AND_NEXT',
  /** 下拉列表向上导航 */
  NAVIGATE_DROPDOWN_UP: 'NAVIGATE_DROPDOWN_UP',
  /** 下拉列表向下导航 */
  NAVIGATE_DROPDOWN_DOWN: 'NAVIGATE_DROPDOWN_DOWN',
  /** 解决当前会话 */
  RESOLVE_CONVERSATION: 'RESOLVE_CONVERSATION',
  /** 前往会话仪表盘 */
  GO_TO_CONVERSATION_DASHBOARD: 'GO_TO_CONVERSATION_DASHBOARD',
  /** 添加附件 */
  ADD_ATTACHMENT: 'ADD_ATTACHMENT',
  /** 前往联系人仪表盘 */
  GO_TO_CONTACTS_DASHBOARD: 'GO_TO_CONTACTS_DASHBOARD',
  /** 切换联系人侧栏 */
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  /** 前往报告侧栏 */
  GO_TO_REPORTS_SIDEBAR: 'GO_TO_REPORTS_SIDEBAR',
  /** 会话列表切换到下一个 Tab */
  MOVE_TO_NEXT_TAB: 'MOVE_TO_NEXT_TAB',
  /** 前往设置 */
  GO_TO_SETTINGS: 'GO_TO_SETTINGS',
  /** 切换到私密备注 */
  SWITCH_TO_PRIVATE_NOTE: 'SWITCH_TO_PRIVATE_NOTE',
  /** 切换到回复 */
  SWITCH_TO_REPLY: 'SWITCH_TO_REPLY',
  /** 切换延后/暂停下拉 */
  TOGGLE_SNOOZE_DROPDOWN: 'TOGGLE_SNOOZE_DROPDOWN',
  /** 打开快捷键弹窗 */
  TOGGLE_MODAL: 'TOGGLE_MODAL',
  /**
   * 会话编辑器内打开快捷回复（canned response）的触发字符。
   * 存的是实际输入字符（如 `/`），不是 tinykeys 组合。
   */
  OPEN_CANNED_RESPONSE: 'OPEN_CANNED_RESPONSE',
};

/**
 * 默认 tinykeys 绑定表，按稳定 action id 索引。
 * keys 格式需与 tinykeys 一致，例如 `Alt+KeyJ`、`$mod+Alt+KeyE`。
 */
export const DEFAULT_SHORTCUTS = Object.freeze({
  [SHORTCUT_ACTIONS.OPEN_PREVIOUS_CONVERSATION]: {
    label: 'OPEN_PREVIOUS_CONVERSATION',
    keys: 'Alt+KeyJ',
  },
  [SHORTCUT_ACTIONS.OPEN_NEXT_CONVERSATION]: {
    label: 'OPEN_NEXT_CONVERSATION',
    keys: 'Alt+KeyK',
  },
  [SHORTCUT_ACTIONS.RESOLVE_AND_NEXT]: {
    label: 'RESOLVE_AND_NEXT',
    keys: '$mod+Alt+KeyE',
  },
  [SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_UP]: {
    label: 'NAVIGATE_DROPDOWN_UP',
    keys: 'ArrowUp',
  },
  [SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_DOWN]: {
    label: 'NAVIGATE_DROPDOWN_DOWN',
    keys: 'ArrowDown',
  },
  [SHORTCUT_ACTIONS.RESOLVE_CONVERSATION]: {
    label: 'RESOLVE_CONVERSATION',
    keys: 'Alt+KeyE',
  },
  [SHORTCUT_ACTIONS.GO_TO_CONVERSATION_DASHBOARD]: {
    label: 'GO_TO_CONVERSATION_DASHBOARD',
    keys: 'Alt+KeyC',
  },
  [SHORTCUT_ACTIONS.ADD_ATTACHMENT]: {
    label: 'ADD_ATTACHMENT',
    keys: '$mod+Alt+KeyA',
  },
  [SHORTCUT_ACTIONS.GO_TO_CONTACTS_DASHBOARD]: {
    label: 'GO_TO_CONTACTS_DASHBOARD',
    keys: 'Alt+KeyV',
  },
  [SHORTCUT_ACTIONS.TOGGLE_SIDEBAR]: {
    label: 'TOGGLE_SIDEBAR',
    keys: 'Alt+KeyO',
  },
  [SHORTCUT_ACTIONS.GO_TO_REPORTS_SIDEBAR]: {
    label: 'GO_TO_REPORTS_SIDEBAR',
    keys: 'Alt+KeyR',
  },
  [SHORTCUT_ACTIONS.MOVE_TO_NEXT_TAB]: {
    label: 'MOVE_TO_NEXT_TAB',
    keys: 'Alt+KeyN',
  },
  [SHORTCUT_ACTIONS.GO_TO_SETTINGS]: {
    label: 'GO_TO_SETTINGS',
    keys: 'Alt+KeyS',
  },
  [SHORTCUT_ACTIONS.SWITCH_TO_PRIVATE_NOTE]: {
    label: 'SWITCH_TO_PRIVATE_NOTE',
    keys: 'Alt+KeyP',
  },
  [SHORTCUT_ACTIONS.SWITCH_TO_REPLY]: {
    label: 'SWITCH_TO_REPLY',
    keys: 'Alt+KeyL',
  },
  [SHORTCUT_ACTIONS.TOGGLE_SNOOZE_DROPDOWN]: {
    label: 'TOGGLE_SNOOZE_DROPDOWN',
    keys: 'Alt+KeyM',
  },
  [SHORTCUT_ACTIONS.TOGGLE_MODAL]: {
    label: 'TOGGLE_MODAL',
    keys: '$mod+Slash',
  },
  [SHORTCUT_ACTIONS.OPEN_CANNED_RESPONSE]: {
    label: 'OPEN_CANNED_RESPONSE',
    // 编辑器触发字符：用户在输入框键入该字符后弹出快捷回复
    keys: '/',
  },
});

/** KeyboardEvent.code / tinykeys 片段 → 展示文案 */
const CODE_DISPLAY_MAP = {
  Slash: KEYS.SLASH,
  ArrowUp: KEYS.UP,
  ArrowDown: KEYS.DOWN,
  Escape: KEYS.ESC,
  Space: 'Space',
  Enter: 'Enter',
  Backspace: 'Backspace',
  Tab: 'Tab',
};

/**
 * 将 tinykeys 绑定字符串转为界面展示芯片文案。
 * 例如 `"Alt+KeyJ"` → `['Alt / ⌥', 'J']`，`"$mod+Alt+KeyE"` → `['Win / ⌘', 'Alt / ⌥', 'E']`。
 * @param {string} keys
 * @returns {string[]}
 */
export function keysToDisplayTokens(keys = '') {
  if (!keys) return [];

  return keys.split('+').map(part => {
    if (part === '$mod') return KEYS.WIN;
    if (part === 'Alt') return KEYS.ALT;
    if (part === 'Shift') return KEYS.SHIFT;
    if (part === 'Control' || part === 'Ctrl') return 'Ctrl';
    if (part === 'Meta') return KEYS.WIN;
    if (part.startsWith('Key') && part.length === 4) return part.slice(3);
    if (part.startsWith('Digit') && part.length === 6) return part.slice(5);
    return CODE_DISPLAY_MAP[part] || part;
  });
}

/**
 * 快捷键弹窗中的展示行。
 * 「打开会话」「下拉导航」等多键动作共用同一标题行，actions 里可挂多个可独立编辑的热键。
 */
export const SHORTCUT_MODAL_ROWS = [
  {
    id: 'OPEN_CONVERSATION',
    label: 'OPEN_CONVERSATION',
    actions: [
      SHORTCUT_ACTIONS.OPEN_PREVIOUS_CONVERSATION,
      SHORTCUT_ACTIONS.OPEN_NEXT_CONVERSATION,
    ],
  },
  {
    id: 'RESOLVE_AND_NEXT',
    label: 'RESOLVE_AND_NEXT',
    actions: [SHORTCUT_ACTIONS.RESOLVE_AND_NEXT],
  },
  {
    id: 'NAVIGATE_DROPDOWN',
    label: 'NAVIGATE_DROPDOWN',
    actions: [
      SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_UP,
      SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_DOWN,
    ],
  },
  {
    id: 'RESOLVE_CONVERSATION',
    label: 'RESOLVE_CONVERSATION',
    actions: [SHORTCUT_ACTIONS.RESOLVE_CONVERSATION],
  },
  {
    id: 'GO_TO_CONVERSATION_DASHBOARD',
    label: 'GO_TO_CONVERSATION_DASHBOARD',
    actions: [SHORTCUT_ACTIONS.GO_TO_CONVERSATION_DASHBOARD],
  },
  {
    id: 'ADD_ATTACHMENT',
    label: 'ADD_ATTACHMENT',
    actions: [SHORTCUT_ACTIONS.ADD_ATTACHMENT],
  },
  {
    id: 'GO_TO_CONTACTS_DASHBOARD',
    label: 'GO_TO_CONTACTS_DASHBOARD',
    actions: [SHORTCUT_ACTIONS.GO_TO_CONTACTS_DASHBOARD],
  },
  {
    id: 'TOGGLE_SIDEBAR',
    label: 'TOGGLE_SIDEBAR',
    actions: [SHORTCUT_ACTIONS.TOGGLE_SIDEBAR],
  },
  {
    id: 'GO_TO_REPORTS_SIDEBAR',
    label: 'GO_TO_REPORTS_SIDEBAR',
    actions: [SHORTCUT_ACTIONS.GO_TO_REPORTS_SIDEBAR],
  },
  {
    id: 'MOVE_TO_NEXT_TAB',
    label: 'MOVE_TO_NEXT_TAB',
    actions: [SHORTCUT_ACTIONS.MOVE_TO_NEXT_TAB],
  },
  {
    id: 'GO_TO_SETTINGS',
    label: 'GO_TO_SETTINGS',
    actions: [SHORTCUT_ACTIONS.GO_TO_SETTINGS],
  },
  {
    id: 'SWITCH_TO_PRIVATE_NOTE',
    label: 'SWITCH_TO_PRIVATE_NOTE',
    actions: [SHORTCUT_ACTIONS.SWITCH_TO_PRIVATE_NOTE],
  },
  {
    id: 'SWITCH_TO_REPLY',
    label: 'SWITCH_TO_REPLY',
    actions: [SHORTCUT_ACTIONS.SWITCH_TO_REPLY],
  },
  {
    id: 'TOGGLE_SNOOZE_DROPDOWN',
    label: 'TOGGLE_SNOOZE_DROPDOWN',
    actions: [SHORTCUT_ACTIONS.TOGGLE_SNOOZE_DROPDOWN],
  },
  {
    id: 'OPEN_CANNED_RESPONSE',
    label: 'OPEN_CANNED_RESPONSE',
    actions: [SHORTCUT_ACTIONS.OPEN_CANNED_RESPONSE],
  },
];

/** 编辑器触发类动作：绑定值是单个输入字符，而非 tinykeys 组合 */
export const EDITOR_TRIGGER_ACTIONS = new Set([
  SHORTCUT_ACTIONS.OPEN_CANNED_RESPONSE,
]);

/** 已被其他 suggestion 占用的触发字符，快捷回复不可选用 */
const RESERVED_EDITOR_TRIGGERS = new Set(['@', ':', '{', '}']);

/** 写入 RegExp 会破坏匹配的字符，录制时拒绝 */
const UNSAFE_EDITOR_TRIGGER_PATTERN = /[.*+?^$()|[\]\\]/;

/** 兼容旧引用的静态列表（由默认配置推导展示） */
export const SHORTCUT_KEYS = SHORTCUT_MODAL_ROWS.map((row, index) => ({
  id: index + 1,
  label: row.label,
  keySet: row.actions.map(action => DEFAULT_SHORTCUTS[action].keys),
  displayKeys: row.actions.flatMap((action, actionIndex) => {
    const tokens = keysToDisplayTokens(DEFAULT_SHORTCUTS[action].keys);
    if (actionIndex === 0) return tokens;
    return [KEYS.SLASH, ...tokens];
  }),
  actions: row.actions,
}));

/**
 * 将 KeyboardEvent 转为 tinykeys 绑定字符串。
 * 纯修饰键（仅 Alt/Ctrl/Meta/Shift）返回 null，不允许设为快捷键。
 * @param {KeyboardEvent} event
 * @returns {string|null}
 */
export function keyboardEventToKeys(event) {
  const { code, key, altKey, shiftKey, ctrlKey, metaKey } = event;
  if (!code) return null;

  const isModifierOnly = [
    'Alt',
    'AltGraph',
    'Control',
    'Meta',
    'Shift',
  ].includes(key);
  if (isModifierOnly) return null;

  const parts = [];
  if (metaKey || ctrlKey) parts.push('$mod');
  if (altKey) parts.push('Alt');
  if (shiftKey) parts.push('Shift');
  parts.push(code);
  return parts.join('+');
}

/**
 * 将 KeyboardEvent 转为编辑器 suggestion 触发字符（如快捷回复的 `/`）。
 * 仅允许单个可打印字符；禁止 Alt/Ctrl/Meta，以及与 @/: 等冲突的字符。
 * @param {KeyboardEvent} event
 * @returns {string|null}
 */
export function keyboardEventToEditorTrigger(event) {
  const { key, altKey, ctrlKey, metaKey } = event;
  if (altKey || ctrlKey || metaKey) return null;
  if (!key || key.length !== 1) return null;
  if (RESERVED_EDITOR_TRIGGERS.has(key)) return null;
  if (UNSAFE_EDITOR_TRIGGER_PATTERN.test(key)) return null;
  if (key === ' ') return null;
  return key;
}

/**
 * 判断动作是否为编辑器触发字符类型。
 * @param {string} action
 * @returns {boolean}
 */
export function isEditorTriggerAction(action) {
  return EDITOR_TRIGGER_ACTIONS.has(action);
}

/**
 * 根据 action 解析冲突提示用的 i18n 文案路径。
 * 分组动作（打开会话 / 下拉导航）回退到共用标题。
 * @param {string} action
 * @returns {{ path: string }}
 */
export function actionTitlePath(action) {
  if (action === SHORTCUT_ACTIONS.TOGGLE_MODAL) {
    return { path: 'KEYBOARD_SHORTCUTS.TOGGLE_MODAL' };
  }
  if (
    action === SHORTCUT_ACTIONS.OPEN_PREVIOUS_CONVERSATION ||
    action === SHORTCUT_ACTIONS.OPEN_NEXT_CONVERSATION
  ) {
    return { path: 'KEYBOARD_SHORTCUTS.TITLE.OPEN_CONVERSATION' };
  }
  if (
    action === SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_UP ||
    action === SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_DOWN
  ) {
    return { path: 'KEYBOARD_SHORTCUTS.TITLE.NAVIGATE_DROPDOWN' };
  }
  return { path: `KEYBOARD_SHORTCUTS.TITLE.${action}` };
}
